import React, { useState } from 'react';
import styled from 'styled-components';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import ConfimationModal from 'components/UI/ConfimationModal';
import * as Arr from 'lib/Array';
import Icon from 'view/Icon';
import { RecommendationSectionItem } from 'data/Product';
import { RECOMMENDATION_STRINGS } from 'constants/productRecommendations';
import { Background, Border, Main, Text } from 'styles/themeColors';

const Table = styled.div`
  border: 1px solid ${Border.main};
  border-radius: 6px;
  overflow: hidden;
`;

const Row = styled.div<{ $isHeader?: boolean }>`
  display: grid;
  grid-template-columns: 32px 1fr 1fr 80px;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid ${Border.light};
  background: ${({ $isHeader }) => ($isHeader ? Background.shaded : Main.white)};
  font-weight: ${({ $isHeader }) => ($isHeader ? 600 : 400)};
  font-size: 14px;
`;

const EmptyRow = styled.div`
  padding: 16px 12px;
  font-size: 14px;
  color: ${Text.placeholder};
  text-align: center;
`;

const HandleCell = styled.div`
  display: flex;
  align-items: center;
  cursor: grab;
`;

const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
`;

// A real button so the delete action has an id, an accessible name and keyboard focus.
const RemoveButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  display: flex;
  cursor: pointer;
`;

const Count = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  color: ${Text.placeholder};
`;

interface Props {
  items: RecommendationSectionItem[];
  onChange: (items: RecommendationSectionItem[]) => void;
  droppableId: string;
}

// Array order is the display order; dragging reorders the array.
const RecommendationProductsTable = ({ items, onChange, droppableId }: Props) => {
  // The row's delete icon asks first; unticking in the picker removes without asking.
  const [removeIndex, setRemoveIndex] = useState<number | null>(null);

  const confirmRemove = () => {
    if (removeIndex !== null) onChange(Arr.removeAtIndex(items, removeIndex));
    setRemoveIndex(null);
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    onChange(Arr.move(items, result.source.index, result.destination.index));
  };

  return (
    <>
      <Table>
        <Row $isHeader>
          <div />
          <div>{RECOMMENDATION_STRINGS.COL_SKU}</div>
          <div>{RECOMMENDATION_STRINGS.COL_PRODUCT_NAME}</div>
          <ActionsCell>{RECOMMENDATION_STRINGS.COL_ACTIONS}</ActionsCell>
        </Row>
        {items.length === 0 ? (
          <EmptyRow>{RECOMMENDATION_STRINGS.NO_PRODUCTS_ADDED}</EmptyRow>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={droppableId}>
              {provided => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {items.map((item, index) => (
                    <Draggable key={item.recommendedProductCode} draggableId={item.recommendedProductCode} index={index}>
                      {dragProvided => (
                        <Row
                          id={`recommendation-product-row-${index}`}
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                        >
                          <HandleCell {...dragProvided.dragHandleProps}>
                            <Icon name="DragHandle" size={16} />
                          </HandleCell>
                          <div>{item.sku}</div>
                          <div>{item.name}</div>
                          <ActionsCell>
                            <RemoveButton
                              type="button"
                              id={`recommendation-product-remove-${index}`}
                              aria-label={RECOMMENDATION_STRINGS.REMOVE_PRODUCT_ROW_LABEL(item.name)}
                              onClick={() => setRemoveIndex(index)}
                            >
                              <Icon name="Trash" size={18} />
                            </RemoveButton>
                          </ActionsCell>
                        </Row>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Table>
      <Count>{RECOMMENDATION_STRINGS.PRODUCTS_ADDED(items.length)}</Count>
      <ConfimationModal
        show={removeIndex !== null}
        onClose={() => setRemoveIndex(null)}
        onConfirm={confirmRemove}
        title={RECOMMENDATION_STRINGS.REMOVE_PRODUCT}
        message={RECOMMENDATION_STRINGS.REMOVE_PRODUCT_CONFIRM_MESSAGE}
        confirmText={RECOMMENDATION_STRINGS.REMOVE_PRODUCT}
      />
    </>
  );
};

export default RecommendationProductsTable;
